import React, { useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { AlertCircle, BarChart3, CheckCircle2, Database, Loader2, PlayCircle, RefreshCw, Route, Sparkles } from "lucide-react";

const DEFAULT_FORM = {
  dataset_path: "eval/ragas_dataset.example.jsonl",
  limit: 10,
  include_traces: true,
  save_traces_path: "eval/ragas_traces.jsonl",
};

const METRIC_ORDER = ["answer_relevancy", "faithfulness", "context_precision", "context_recall"];

function formatMetricValue(value) {
  if (typeof value !== "number" || Number.isNaN(value)) return "-";
  return value.toFixed(3);
}

function formatKey(key) {
  return key
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildMetricSummaries(metrics = []) {
  const buckets = new Map();

  metrics.forEach((row) => {
    Object.entries(row || {}).forEach(([key, value]) => {
      if (typeof value !== "number" || Number.isNaN(value)) return;
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(value);
    });
  });

  const summaries = Array.from(buckets.entries()).map(([key, values]) => {
    const total = values.reduce((sum, value) => sum + value, 0);
    const average = values.length ? total / values.length : 0;
    return {
      key,
      label: formatKey(key),
      average,
      min: values.length ? Math.min(...values) : 0,
      max: values.length ? Math.max(...values) : 0,
      count: values.length,
    };
  });

  summaries.sort((a, b) => {
    const indexA = METRIC_ORDER.indexOf(a.key);
    const indexB = METRIC_ORDER.indexOf(b.key);
    if (indexA === -1 && indexB === -1) return a.label.localeCompare(b.label);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return summaries;
}

function countRoutes(traces = []) {
  return traces.reduce((acc, trace) => {
    const route = trace && trace.route ? trace.route : "unknown";
    acc[route] = (acc[route] || 0) + 1;
    return acc;
  }, {});
}

function InfoCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 break-all text-sm font-medium text-slate-800">{value}</div>
    </div>
  );
}

function MetricPill({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/15 bg-white/10 px-3 py-3 backdrop-blur-sm">
      <div className="text-[11px] uppercase tracking-wide text-slate-300">{label}</div>
      <div className="mt-1 text-sm font-semibold text-white">{value}</div>
    </div>
  );
}

function StatBox({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-2 py-2 text-center">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function Badge({ tone = "slate", children }) {
  const tones = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-600 text-white",
    gray: "bg-slate-200 text-slate-700",
  };

  return <span className={"inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold " + (tones[tone] || tones.slate)}>{children}</span>;
}

function RagasEvaluationPage() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const metricSummaries = useMemo(() => buildMetricSummaries(result && result.metrics ? result.metrics : []), [result]);
  const traces = result && result.traces ? result.traces : [];
  const routeCounts = useMemo(() => countRoutes(traces), [traces]);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleUseExample = () => {
    setForm((prev) => ({
      ...prev,
      dataset_path: "eval/ragas_dataset.example.jsonl",
      limit: 10,
      include_traces: true,
      save_traces_path: "eval/ragas_traces.jsonl",
    }));
  };

  const handleReset = () => {
    setForm(DEFAULT_FORM);
    setError("");
    setResult(null);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    const payload = {
      dataset_path: form.dataset_path ? form.dataset_path.trim() : undefined,
      limit: form.limit === "" || form.limit === null ? undefined : Number(form.limit),
      include_traces: Boolean(form.include_traces),
      save_traces_path: form.save_traces_path ? form.save_traces_path.trim() : undefined,
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_API_BASE_URL + "/admin/ragas/evaluate",
        payload,
        { withCredentials: true }
      );

      setResult(response.data);
      Swal.fire("Berhasil", (response.data && response.data.message) || "Evaluasi RAGAS selesai dijalankan.", "success");
    } catch (err) {
      const message =
        (err.response && err.response.data && (err.response.data.detail || err.response.data.message || err.response.data.error)) ||
        "Gagal menjalankan evaluasi.";
      setError(message);
      Swal.fire("Oops!", message, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tw w-full space-y-6 px-2 lg:px-0">
      <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 p-5 text-white shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold text-slate-100">
              <Sparkles size={14} />
              Admin Tools
            </div>
            <h3 className="text-2xl font-semibold lg:text-3xl">Evaluasi RAGAS</h3>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-200/90">
              Jalankan evaluasi kualitas RAG untuk melihat faithfulness, answer relevancy, context precision, dan context recall dari pipeline yang sedang aktif.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm lg:min-w-[320px]">
            <MetricPill label="Endpoint" value="/admin/ragas/evaluate" />
            <MetricPill label="Mode" value="Admin only" />
            <MetricPill label="Output" value="JSON + traces" />
            <MetricPill label="Engine" value="RAG + Qdrant" />
          </div>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center gap-2 text-slate-800">
            <BarChart3 size={18} />
            <h4 className="text-lg font-semibold">Konfigurasi Evaluasi</h4>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Dataset Path</label>
              <input
                type="text"
                name="dataset_path"
                value={form.dataset_path}
                onChange={handleChange}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                placeholder="eval/ragas_dataset.example.jsonl"
              />
              <p className="mt-1 text-xs text-slate-500">Gunakan path relatif dari folder backend-chat atau path absolut jika perlu.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Limit Sample</label>
                <input
                  type="number"
                  min="1"
                  name="limit"
                  value={form.limit}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Save Traces Path</label>
                <input
                  type="text"
                  name="save_traces_path"
                  value={form.save_traces_path}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10"
                  placeholder="eval/ragas_traces.jsonl"
                />
              </div>
            </div>

            <label className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <input
                type="checkbox"
                name="include_traces"
                checked={form.include_traces}
                onChange={handleChange}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900"
              />
              <span>
                <span className="block font-semibold text-slate-800">Include traces</span>
                <span className="block text-xs text-slate-500">Tampilkan daftar trace, route, dan sub-question pada hasil evaluasi.</span>
              </span>
            </label>

            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {loading ? <Loader2 size={16} className="animate-spin" /> : <PlayCircle size={16} />}
                {loading ? "Menjalankan evaluasi..." : "Run Evaluation"}
              </button>

              <button
                type="button"
                onClick={handleUseExample}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <Database size={16} />
                Gunakan Contoh Dataset
              </button>

              <button
                type="button"
                onClick={handleReset}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RefreshCw size={16} />
                Reset
              </button>
            </div>
          </form>

          {error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              <div className="flex items-start gap-2">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{error}</span>
              </div>
            </div>
          ) : null}
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-800">
              <CheckCircle2 size={18} />
              <h4 className="text-lg font-semibold">Ringkasan Hasil</h4>
            </div>

            {result ? (
              <div className="space-y-4">
                <div className="grid gap-3 sm:grid-cols-2">
                  <InfoCard label="Dataset" value={result.dataset_path || "-"} />
                  <InfoCard label="Samples" value={String(result.sample_count || 0)} />
                  <InfoCard label="Message" value={result.message || "-"} />
                  <InfoCard label="Saved traces" value={result.saved_traces_path || "-"} />
                </div>

                {metricSummaries.length > 0 ? (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-1">
                    {metricSummaries.map((item) => (
                      <div key={item.key} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="text-sm font-semibold text-slate-800">{item.label}</div>
                            <div className="text-xs text-slate-500">{item.count} sample</div>
                          </div>
                          <div className="rounded-full bg-slate-900 px-3 py-1 text-sm font-semibold text-white">
                            {formatMetricValue(item.average)}
                          </div>
                        </div>
                        <div className="mt-3 grid grid-cols-3 gap-2 text-xs text-slate-600">
                          <StatBox label="Min" value={formatMetricValue(item.min)} />
                          <StatBox label="Max" value={formatMetricValue(item.max)} />
                          <StatBox label="Avg" value={formatMetricValue(item.average)} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                    Belum ada metrik yang bisa ditampilkan.
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Jalankan evaluasi untuk melihat ringkasan metrik di sini.
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-2 text-slate-800">
              <Route size={18} />
              <h4 className="text-lg font-semibold">Route & Trace Log</h4>
            </div>

            {traces.length > 0 ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2 text-xs">
                  {Object.entries(routeCounts).map(([route, count]) => (
                    <span key={route} className="rounded-full bg-slate-100 px-3 py-1 font-semibold text-slate-700">
                      {route}: {count}
                    </span>
                  ))}
                </div>

                <div className="max-h-[520px] space-y-3 overflow-y-auto pr-1">
                  {traces.map((trace, index) => (
                    <div key={(trace.question || "trace") + "-" + index} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Question #{index + 1}</div>
                          <p className="mt-1 text-sm leading-6 text-slate-800">{trace.question || "-"}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 md:justify-end">
                          <Badge tone="slate">Route: {trace.route || "-"}</Badge>
                          <Badge tone={trace.used_subquestions ? "emerald" : "gray"}>
                            Sub-Question {trace.used_subquestions ? "ON" : "OFF"}
                          </Badge>
                        </div>
                      </div>

                      {Array.isArray(trace.subquestions) && trace.subquestions.length > 0 ? (
                        <details className="mt-3 rounded-xl border border-slate-200 bg-white px-3 py-2">
                          <summary className="cursor-pointer list-none text-sm font-semibold text-slate-700">
                            Sub-Questions ({trace.subquestions.length})
                          </summary>
                          <ul className="mt-2 space-y-2 text-sm text-slate-600">
                            {trace.subquestions.map((item, subIndex) => (
                              <li key={index + "-" + subIndex} className="rounded-lg bg-slate-50 px-3 py-2">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </details>
                      ) : null}

                      {Array.isArray(trace.sources) && trace.sources.length > 0 ? (
                        <div className="mt-3">
                          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sources</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {trace.sources.slice(0, 5).map((source) => (
                              <span key={source} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                                {source}
                              </span>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
                Trace log akan muncul setelah evaluasi selesai.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RagasEvaluationPage;
